import { useEffect, useRef, useState } from 'react';
import type { AnalyticsOverview, ReportingPeriod } from '@raptor/contracts';
import { ApiResponseError, apiClient } from '../lib/api-client.js';

type AnalyticsWorkspaceMode = 'OVERVIEW' | 'BRANCHES_TEAM' | 'OWNER_SUMMARY';

const periodLabels: Record<ReportingPeriod, string> = {
  TODAY: 'Today',
  YESTERDAY: 'Yesterday',
  LAST_7_DAYS: 'Last 7 days',
  LAST_30_DAYS: 'Last 30 days',
};

function MetricList({ items }: { items: Array<{ label: string; value: string | number }> }) {
  return items.length ? <ul className="metric-list">{items.map((item) => (
    <li key={item.label}><span>{item.label}</span><strong>{item.value}</strong></li>
  ))}</ul> : <p>No persisted activity is available in this reporting scope.</p>;
}

export function AnalyticsWorkspace({
  organizationId,
  period,
  onPeriodChange,
  mode = 'OVERVIEW',
}: {
  organizationId: string;
  period: ReportingPeriod;
  onPeriodChange(period: ReportingPeriod): void;
  mode?: AnalyticsWorkspaceMode;
}) {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);

  useEffect(() => {
    const id = ++request.current;
    setOverview(null);
    setError(null);
    void apiClient.getAnalyticsOverview(organizationId, period).then((data) => {
      if (id === request.current) setOverview(data);
    }).catch((reason) => {
      if (id !== request.current) return;
      setError(reason instanceof ApiResponseError && reason.status === 403
        ? 'Analytics access is not available for this workspace role.'
        : 'Analytics are temporarily unavailable.');
    });
    return () => { request.current += 1; };
  }, [organizationId, period]);

  const title = mode === 'BRANCHES_TEAM' ? 'Branches & Team' : mode === 'OWNER_SUMMARY' ? 'Cross-module intelligence' : 'Analytics';
  const description = mode === 'BRANCHES_TEAM'
    ? 'Authorized branch performance and current team workload derived from persisted operational records.'
    : 'Observed acquisition facts and CRM operational associations. Relationships shown here do not assert causation.';

  return <section className={`workspace-rail analytics-workspace analytics-workspace--${mode.toLowerCase()}`} aria-labelledby={`analytics-title-${mode}`}>
    <div className="rail-heading">
      <div><p className="eyebrow">Owner Intelligence</p><h2 id={`analytics-title-${mode}`}>{title}</h2></div>
      <label className="period-control">Reporting period<select aria-label={`${title} reporting period`} value={period} onChange={(event) => onPeriodChange(event.target.value as ReportingPeriod)}>{Object.entries(periodLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
    </div>
    <p className="analytics-workspace__intro">{description} Reporting windows use Africa/Cairo; persisted timestamps remain UTC.</p>
    {error ? <p className="notice notice--error" role="alert">{error}</p> : null}
    {!overview && !error ? <p className="dashboard__loading">Loading persisted intelligence…</p> : null}
    {overview ? <>
      {mode !== 'BRANCHES_TEAM' ? <dl className="kpi-grid">
        <div><dt>Observed sessions</dt><dd>{overview.acquisition.sessions}</dd><small>{overview.acquisition.identifiedSessions} exactly linked to a customer</small></div>
        <div><dt>CRM leads</dt><dd>{overview.acquisition.crmLeadsCreated}</dd><small>{overview.acquisition.wonLeads} won · {overview.acquisition.lostLeads} lost in period</small></div>
        <div><dt>Completed order events</dt><dd>{overview.acquisition.sessionsWithCompletedOrder}</dd><small>First-party observations, not inferred revenue</small></div>
        <div><dt>Unhandled conversations</dt><dd>{overview.communications.unhandled}</dd><small>Current operational queue</small></div>
      </dl> : null}

      {mode === 'OWNER_SUMMARY' ? <p className="analytics-evidence-note">Session, channel, and order facts are directly observed. Lead and Customer associations are CRM operational links. Campaign attribution remains unavailable until a persisted campaign reference exists.</p> : null}

      {mode !== 'OWNER_SUMMARY' ? <div className="dashboard-grid analytics-grid">
        {mode === 'OVERVIEW' ? <>
          <section className="dashboard-panel"><h3>Observed path coverage</h3><MetricList items={[
            { label: 'Website sessions', value: overview.acquisition.sessions },
            { label: 'Exactly identified sessions', value: overview.acquisition.identifiedSessions },
            { label: 'Identified customers with a CRM Lead', value: overview.acquisition.sessionCustomersWithLead },
            { label: 'Sessions with completed order event', value: overview.acquisition.sessionsWithCompletedOrder },
          ]} /><p className="analytics-evidence-note">This is an evidence path, not a causal conversion funnel.</p></section>
          <section className="dashboard-panel"><h3>Channel contribution</h3><MetricList items={overview.acquisition.byChannel.map((channel) => ({ label: channel.sourceKind.replace('_', ' '), value: `${channel.conversations} conversations · ${channel.inboundEvents} events` }))} /><h3>CRM Lead sources</h3><MetricList items={overview.acquisition.bySource.map((source) => ({ label: source.label, value: source.count }))} />{overview.acquisition.bySourceTruncated ? <p>Showing the first 100 Lead sources.</p> : null}</section>
          <section className="dashboard-panel"><h3>Relationships & follow-up</h3><MetricList items={[
            { label: 'Corporate companies', value: overview.corporate.companies },
            { label: 'Corporate open opportunities', value: overview.corporate.openOpportunities },
            { label: 'Corporate overdue tasks', value: overview.corporate.overdueTasks },
            { label: 'Insurance providers', value: overview.insurance.providers },
            { label: 'Insurance overdue follow-ups', value: overview.insurance.overdueFollowUps },
          ]} /></section>
          <section className="dashboard-panel"><h3>Catalog & Loyalty</h3><MetricList items={[
            { label: 'Catalog imports', value: overview.catalog.imports },
            { label: 'Imports needing review', value: overview.catalog.importsNeedingReview },
            { label: 'Catalog items', value: overview.catalog.items },
            { label: 'Loyalty accounts', value: overview.loyalty.accounts },
            { label: 'Current points balance', value: overview.loyalty.pointsBalance },
          ]} /></section>
          <section className="dashboard-panel"><h3>Automation & Communications</h3><MetricList items={[
            { label: 'Enabled rules', value: overview.automation.enabledRules },
            { label: 'Successful runs', value: overview.automation.succeededRuns },
            { label: 'Failed or retryable runs', value: overview.automation.failedRuns },
            { label: 'Inbound events', value: overview.communications.inboundEvents },
            { label: 'Unhandled conversations', value: overview.communications.unhandled },
          ]} /></section>
          <section className="dashboard-panel"><h3>Provider intake & Journeys</h3><MetricList items={[
            { label: 'Website events', value: overview.providerIntake.websiteEvents },
            { label: 'Provider-compatible inbound events', value: overview.providerIntake.inboundEvents },
            { label: 'Journey sessions', value: overview.journeys.sessions },
            { label: 'First-party events', value: overview.journeys.firstPartyEvents },
            { label: 'Open identity reviews', value: overview.journeys.openIdentityConflicts },
          ]} /><p className="analytics-evidence-note">Latest inbound receipt: {overview.providerIntake.latestReceivedAt ? new Date(overview.providerIntake.latestReceivedAt).toLocaleString() : 'No persisted receipt'}</p></section>
        </> : null}
        {mode === 'BRANCHES_TEAM' ? <>
          <section className="dashboard-panel"><h3>Branch performance</h3>{overview.branches.length ? <ul className="analytics-entity-list">{overview.branches.map((branch) => <li key={branch.id}><strong>{branch.label}</strong><span>{branch.customersCreated} customers · {branch.leadsCreated} Leads created</span><span>{branch.openTasks} open · {branch.overdueTasks} overdue tasks</span></li>)}</ul> : <p>No authorized branches are available.</p>}{overview.branchesTruncated ? <p>Showing the first 100 authorized branches.</p> : null}</section>
          <section className="dashboard-panel"><h3>Current team workload</h3>{overview.team.length ? <ul className="analytics-entity-list">{overview.team.map((member) => <li key={member.membershipId}><strong>{member.displayName ?? 'Verified member'}</strong><span>{member.role.replace('_', ' ')}</span><span>{member.assignedLeads} assigned Leads · {member.openTasks} open · {member.overdueTasks} overdue</span></li>)}</ul> : <p>No authorized team members are available.</p>}{overview.teamTruncated ? <p>Showing the first 500 authorized team members.</p> : null}</section>
        </> : null}
      </div> : null}
    </> : null}
  </section>;
}
